import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { confirmPasswordReset } from '@/features/auth/api'
import { toast } from '@/lib/toast'

// 8 characters is PRD section 9; 72 bytes is bcrypt's ceiling, enforced server-side too.
// Both are checked here so a long password fails while it is still on screen to fix.
const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Use at least 8 characters')
      .refine((value) => new TextEncoder().encode(value).length <= 72, {
        message: 'That is too long — 72 bytes at most',
      }),
    confirm: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'The two passwords do not match',
  })

type FormValues = z.infer<typeof schema>

export function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const onSubmit = handleSubmit(async ({ password }) => {
    setFormError(null)
    try {
      const message = await confirmPasswordReset(token, password)
      toast.success(message)
      navigate('/login', { replace: true })
    } catch (error) {
      // An expired or already-used token is the common failure, and the server says so.
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Check that the API is running.',
      )
    }
  })

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Choose a new password"
      footer={
        <p className="text-center text-sm">
          <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-400">
            Back to sign in
          </Link>
        </p>
      }
    >
      {token === '' ? (
        <Alert tone="error" title="This link is incomplete">
          It is missing its reset token — it may have been cut short by an email client.{' '}
          <Link to="/forgot-password" className="underline">
            Request a new link
          </Link>
          .
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="New password" error={errors.password?.message}>
            {(props) => (
              <Input
                {...props}
                {...register('password')}
                type="password"
                autoComplete="new-password"
                autoFocus
                placeholder="••••••••"
              />
            )}
          </Field>

          <Field label="Confirm new password" error={errors.confirm?.message}>
            {(props) => (
              <Input
                {...props}
                {...register('confirm')}
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
              />
            )}
          </Field>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Set new password'}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
